import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Table,
    Input,
    DatePicker,
    Select,
    Button,
    Space,
    Card,
    Modal,
    Form,
    Switch,
    Pagination,
    Tooltip,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import ViewTableBtn from "../../assets/images/view-icon.svg";
import EditTableBtn from "../../assets/images/action-edit-btn.svg";
import DeleteTableBtn from "../../assets/images/delete-bin-icon.svg"; // Checked PostTabs.js for icon name
import DeleteModalBtn from "../../assets/images/delete-icon-modal.svg";
import TextArea from 'antd/lib/input/TextArea';
import useRequest from '../../hooks/useRequest';
import lang from "../../helper/langHelper";
import moment from 'moment';
import { Severty, ShowToast } from '../../helper/toast';

const { Option } = Select;

// Permission helper
const getPermissionByName = (moduleName) => {
    try {
        const user = JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
        if (user.type === "Admin") {
            return { view: true, add: true, edit: true, delete: true };
        }
        // Case/whitespace-insensitive match — see BlogTabs.js's identical fix.
        const normalizedTarget = String(moduleName || "").trim().toLowerCase();
        const permissions = user?.roleId?.permission || [];
        return (
          permissions.find(
            (p) => String(p?.name || "").trim().toLowerCase() === normalizedTarget
          ) || {}
        );
    } catch {
        return {};
    }
};

// Only the note's creator can mark/unmark it private — the backend already
// enforces this (silently ignores the change otherwise), but the toggle is
// disabled here too so Admin doesn't see it flip and get a false "updated"
// success message on someone else's note.
const getCurrentUserId = () => {
    try {
        const user = JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
        return String(user?._id || user?.id || "");
    } catch {
        return "";
    }
};

const ManageNotesTabs = () => {
    const { id } = useParams(); // Client ID from route
    const [data, setData] = useState([]);
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [form] = Form.useForm();
    const { request } = useRequest();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editData, setEditData] = useState(null);
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('');

    // Permission hook for notes operations
    const notesPermissions = getPermissionByName("Notes"); // Assuming "Notes" is the permission name

    const fetchNotes = () => {
        // If permission name is different, this might block. Assuming "Notes" or allowing Admin default.
        // if (!notesPermissions.view) {
        //   // ShowToast("You don't have permission to view notes!", Severty.ERROR);
        //   // return;
        // }
        setLoading(true);
        let query = [];
        if (page) query.push(`page=${page}`);
        if (limit) query.push(`limit=${limit}`);
        if (searchText) query.push(`name=${encodeURIComponent(searchText)}`);
        if (selectedDate) query.push(`date=${moment(selectedDate).format('YYYY-MM-DD')}`);
        if (sortField && sortOrder) query.push(`sortField=${sortField}&sortOrder=${sortOrder}`);
        const queryString = query.length ? `?${query.join('&')}` : '';

        request({
            url: `/admin/access-module/notes/${id}${queryString}`,
            method: "GET",
            onSuccess: (res) => {
                setData(res.data.Notes.items || []);
                setTotal(res.data.Notes.total || 0); // Corrected path based on controller response structure
                setLoading(false);
            },
            onError: () => {
                setData([]);
                setTotal(0);
                setLoading(false);
            },
        });
    };

    const handleDelete = (record) => {
        if (!notesPermissions.delete) {
            ShowToast("You don't have permission to delete notes!", Severty.ERROR);
            return;
        }
        setDeleteLoading(true);
        request({
            url: `/admin/access-module/notes/${record._id}`,
            method: "DELETE",
            onSuccess: () => {
                ShowToast("Notes deleted successfully", Severty.SUCCESS);
                fetchNotes();
                setDeleteModalVisible(false);
            },
            onError: () => {
                ShowToast("Failed to delete notes", Severty.ERROR);
            },
            onFinally: () => setDeleteLoading(false),
        });
    };

    useEffect(() => {
        fetchNotes();
    }, [page, searchText, selectedDate, sortField, sortOrder]);

    const handleSearch = (value) => {
        setPage(1);
        setSearchText(value);
    };

    const handleDateChange = (date) => {
        setPage(1);
        setSelectedDate(date);
    };

    const handlePageChange = (p) => {
        setPage(p);
    };

    const handleTableChange = (pagination, filters, sorter) => {
        if (sorter.field && sorter.order) {
            setSortField(sorter.field);
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        } else {
            setSortField('');
            setSortOrder('');
        }
        setPage(1);
    };

    const handleView = (record) => {
        setSelectedNote(record);
        setViewModalVisible(true);
    };

    const handleEdit = (record) => {
        if (!notesPermissions.edit) {
            ShowToast("You don't have permission to edit notes!", Severty.ERROR);
            return;
        }
        form.setFieldsValue({
            title: record.title,
            content: record.content,
            link: record.link,
            isPrivate: record.isPrivate,
        });
        setEditData(record);
        setIsEditMode(true);
        setAddModalVisible(true);
    };

    const handleAddNew = () => {
        if (!notesPermissions.add) {
            ShowToast("You don't have permission to add notes!", Severty.ERROR);
            return;
        }
        form.resetFields();
        setAddModalVisible(true);
    };

    const handleSubmit = (values) => {
        if (!notesPermissions.add && !notesPermissions.edit) {
            ShowToast("You don't have permission to save notes!", Severty.ERROR);
            return;
        }
        setLoading(true);
        request({
            url: isEditMode ? `/admin/access-module/notes/edit-notes/${editData._id}` : `/admin/access-module/notes/${id}`,
            method: isEditMode ? "PUT" : "POST",
            data: { ...values },
            onSuccess: (data) => {
                if (data.status === 200) {
                    ShowToast(`Notes ${isEditMode ? "updated" : "created"} successfully`, Severty.SUCCESS);
                    setAddModalVisible(false);
                    setEditData(null);
                    setIsEditMode(false);
                    fetchNotes();
                } else {
                    ShowToast(data.message, Severty.ERROR);
                }
            },
            onError: () => {
                ShowToast(`Error ${isEditMode ? "updating" : "creating"} notes`, Severty.ERROR);
            },
            onFinally: () => setLoading(false),
        });
    };

    // Helper for visibility toggle if needed in Admin (maybe redundant if Admin sees all, but keeping logic)
    // Admin might allow toggling privacy on behalf of brand/user?
    // For now, removing the Switch column if Admin doesn't need to toggle it directly in table, 
    // or keeping it if Admin wants to control it. The controller has `isPrivate` in edit, so we can keep it.

    const columns = [
        {
            title: lang('Title'),
            dataIndex: 'title',
            key: 'title',
            sorter: (a, b) => {
                if (!a.title || !b.title) return 0;
                return a.title.localeCompare(b.title);
            },
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: null,
        },
        {
            title: lang('Created On'),
            dataIndex: "created_at",
            key: "created_at",
            sorter: (a, b) => {
                if (!a.created_at || !b.created_at) return 0;
                return moment(a.created_at).unix() - moment(b.created_at).unix();
            },
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: null,
            render: (created_at) => moment(created_at).format("DD-MM-YYYY"),
        },
        {
            title: lang('Last Edited'),
            dataIndex: 'updated_at',
            key: "updated_at",
            sorter: (a, b) => {
                if (!a.updated_at || !b.updated_at) return 0;
                return moment(a.updated_at).unix() - moment(b.updated_at).unix();
            },
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: null,
            render: (updated_at) => moment(updated_at).format("DD-MM-YYYY"),
        },
        {
            title: lang('Private'),
            key: 'visibility',
            dataIndex: 'isPrivate',
            render: (isPrivate) => (
                <span>{isPrivate ? "Yes" : "No"}</span>
            ),
        },
        {
            title: lang('ACTION'),
            key: 'action',
            render: (_, record) => {
                return (
                    <Space>
                        <Tooltip title={lang("view")}>
                            <Button className='table-light-fill-btn' onClick={() => handleView(record)}>
                                <img src={ViewTableBtn} alt="View" />
                            </Button>
                        </Tooltip>
                        {notesPermissions.edit && (
                            <Tooltip title={lang("edit")}>
                                <Button className='table-primary-fill-btn' onClick={() => handleEdit(record)}>
                                    <img src={EditTableBtn} alt="Edit" />
                                </Button>
                            </Tooltip>
                        )}
                        {notesPermissions.delete && (
                            <Tooltip title={lang("delete")}>
                                <Button className='table-danger-fill-btn' onClick={() => { setSelectedNote(record); setDeleteModalVisible(true); }}>
                                    <img src={DeleteTableBtn} alt="Delete" />
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Card
                title={lang("Notes Manager")}
                bordered={false}
                className="criclebox tablespace mb-24"
                extra={
                    <div className="d-flex align-items-end gap-3 flex-wrap">
                        <div className="pageHeadingSearch">
                            <Input.Search
                                placeholder={lang("Search")}
                                className="searchInput"
                                allowClear
                                onSearch={handleSearch}
                                onChange={e => handleSearch(e.target.value)}
                                value={searchText}
                            />
                        </div>
                        <div className="role-wrap">
                            <DatePicker
                                placeholder={lang('Date')}
                                value={selectedDate}
                                onChange={handleDateChange}
                                allowClear
                            />
                        </div>
                        {notesPermissions.add && (
                            <div className="role-wrap">
                                <Button
                                    className="add-table-modal"
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddNew}
                                >
                                    {lang("Create New Note")}
                                </Button>
                            </div>
                        )}
                    </div>
                }
            >
                <div className='table-responsive'>
                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        pagination={false}
                        rowKey="_id"
                        onChange={handleTableChange}
                    />
                    <div style={{ marginTop: 16, textAlign: "center" }}>
                        <Pagination
                            current={page}
                            total={total}
                            pageSize={limit}
                            showSizeChanger={false}
                            onChange={handlePageChange}
                        />
                    </div>
                </div>
            </Card>

            {/* View Modal */}
            <Modal
                width={757}
                className="table_modal"
                title={lang("Notes Detail")}
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={null}
            >
                <div className="note-detail">
                  <div className="note-detail-meta">
                    <div className="note-detail-item"><span className="note-detail-label">{lang("Title")}</span><span className="note-detail-value">{selectedNote?.title || "—"}</span></div>
                    <div className="note-detail-item"><span className="note-detail-label">{lang("Link")}</span><span className="note-detail-value">{selectedNote?.link || "—"}</span></div>
                    <div className="note-detail-item"><span className="note-detail-label">{lang("Notes Status")}</span><span className={`note-detail-pill ${selectedNote?.isPrivate ? "is-private" : "is-public"}`}>{selectedNote?.isPrivate ? lang("Private") : lang("Public")}</span></div>
                  </div>
                  <div className="note-detail-content">
                    <span className="note-detail-label">{lang("Content")}</span>
                    <div className="note-detail-body">{selectedNote?.content || "—"}</div>
                  </div>
                </div>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal
                width={757}
                className="table_modal"
                title={isEditMode ? lang("Edit Note") : lang("Add Note")}
                open={addModalVisible}
                footer={false}
                onCancel={() => setAddModalVisible(false)}
            >
                <Form layout="vertical" form={form} onFinish={handleSubmit}>
                    <Form.Item
                        label={lang("Add Title")}
                        name="title"
                        rules={[{ required: true, whitespace: true, message: lang("Please enter the Title") }]}
                    >
                        <Input placeholder={lang("Enter title here")} />
                    </Form.Item>
                    <Form.Item
                        label={lang("Content")}
                        name="content"
                        rules={[{ required: true, whitespace: true, message: lang("Please enter notes") }]}
                    >
                        <TextArea placeholder={lang("Please enter notes")} />
                    </Form.Item>
                    <Form.Item
                        label={lang("Link")}
                        name="link"
                        rules={[{ required: true, message: lang("Please select link") }]}
                    >
                        <Select placeholder={lang("Select Link")}>
                            <Option value="Calendar">{lang("Calendar")}</Option>
                            <Option value="Blog">{lang("Blog")}</Option>
                            <Option value="Report">{lang("Report")}</Option>
                        </Select>
                    </Form.Item>
                    <div className='note-add-footer'>
                        <Form.Item
                            label={lang("Private")}
                            name="isPrivate"
                            valuePropName="checked"
                        >
                            <Switch
                                disabled={
                                    isEditMode &&
                                    !!editData?.uploadedby &&
                                    String(editData.uploadedby?._id || editData.uploadedby) !== getCurrentUserId()
                                }
                            />
                        </Form.Item>
                    </div>
                    <Form.Item>
                        <div className="action-footer-buttons">
                            <Button onClick={() => setAddModalVisible(false)} className="schedule-btn">{lang("Cancel")}</Button>
                            <Button htmlType='submit' className="submitted-btn">{lang("Save")}</Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Delete Modal */}
            <Modal
                className="table_modal delete-modal"
                footer={false}
                title={false}
                width={450}
                open={deleteModalVisible}
                onCancel={() => setDeleteModalVisible(false)}
            >
                <div className="delete-modal-body">
                    <img src={DeleteModalBtn} alt="delete" />
                    <h3>{lang("Are you sure?")}</h3>
                    <p>{lang("Are you sure you want to delete this note?")}</p>
                    <Button className="delete-fill-btn" onClick={() => handleDelete(selectedNote)}>
                        {lang("Yes, confirm")}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default ManageNotesTabs;
